/**
 * What a roll toast says (`DiceOverlay`), projected from a throw request: who rolled, what the roll
 * was for, the notation and the total, and a crit/miss tone where the app already decides one.
 * Pure, so the readout's wording is testable without mounting the overlay.
 */
import type { ResolvedStep } from "@lancer-vtt/domain";
import type { DiceThrowRequest } from "./diceController";
import { formatStepsSummary } from "./resolutionDice";
import { formatRollSummary } from "./rollChoreography";

export type RollToastContent = {
  /** The rolling slot, resolved to a name by the overlay; null when the request carries none. */
  authorSlotId: string | null;
  /** What the roll was for (`"AGILITY"`), or a kind word for a combat resolution. */
  label: string | null;
  /** The notation or roll description. */
  detail: string;
  /** The big number; null when `detail` already carries the numbers (a combat resolution). */
  total: number | null;
  /** A critical hit or a miss, as the engine recorded it. */
  tone: "crit" | "miss" | null;
};

function stepsTone(steps: readonly ResolvedStep[]): RollToastContent["tone"] {
  for (const step of steps) {
    if (step.op !== "to_hit") continue;
    if (step.result === "crit") return "crit";
    if (step.result === "miss") return "miss";
  }
  return null;
}

/** The toast for a throw, or null for one with no readout (the dev `d20`, a dice-less resolution). */
export function rollToastContent(req: DiceThrowRequest): RollToastContent | null {
  switch (req.kind) {
    case "d20":
      return null;
    case "feedRoll":
      return { authorSlotId: req.entry.authorSlotId, label: req.entry.label ?? null, detail: req.entry.notation, total: req.entry.total, tone: null };
    case "roll": {
      const summary = formatRollSummary(req.result);
      return { authorSlotId: null, label: null, detail: summary.slice(0, summary.lastIndexOf(" = ")), total: req.result.total, tone: req.result.descriptor.crit ? "crit" : null };
    }
    case "steps": {
      const summary = formatStepsSummary(req.steps);
      return summary === null ? null : { authorSlotId: null, label: "COMBAT", detail: summary, total: null, tone: stepsTone(req.steps) };
    }
  }
}
